import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Alert
} from '@mui/material';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AvailabilityPreferences from '../components/AvailabilityPreferences';
import PasskeyManagement from '../components/PasskeyManagement';
import AuthorizedReporters from '../components/AuthorizedReporters';
import { useAuth } from '../context/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`account-tabpanel-${index}`}
      aria-labelledby={`account-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `account-tab-${index}`,
    'aria-controls': `account-tabpanel-${index}`,
  };
};

const AccountSettingsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isAgency, agencyLoginEnabled } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Parse the initial tab from URL search params or location state
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    const locationState = location.state as { tab?: string } | null;

    if (tab === 'availability-preferences' || locationState?.tab === 'availability-preferences') {
      setTabValue(0);
    } else if (tab === 'passkeys' || locationState?.tab === 'passkeys') {
      setTabValue(1);
    } else if (tab === 'authorized-reporters' || locationState?.tab === 'authorized-reporters') {
      setTabValue(2);
    }
  }, [location]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Update URL to reflect current tab
    const searchParams = new URLSearchParams();
    if (newValue === 0) {
      searchParams.set('tab', 'availability-preferences');
    } else if (newValue === 1) {
      searchParams.set('tab', 'passkeys');
    } else if (newValue === 2) {
      searchParams.set('tab', 'authorized-reporters');
    }
    navigate(`/account-settings?${searchParams.toString()}`, { replace: true });
  };

  if (!isAuthenticated) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Header />
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          flex: '1 0 auto',
          gap: 2,
          p: 4
        }}>
          <CircularProgress size={48} />
          <Typography variant="h6">Redirecting to login...</Typography>
        </Box>
        <Footer />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />
      
      <Container maxWidth="lg" sx={{ py: 3, flex: '1 0 auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Typography variant="h4" component="h1" gutterBottom>
          Account Settings
        </Typography>

        <Paper sx={{ width: '100%', mt: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="account settings tabs">
              <Tab label="Availability Preferences" {...a11yProps(0)} />
              <Tab label="Passkeys" {...a11yProps(1)} />
              {isAgency && agencyLoginEnabled && <Tab label="Manage Agency" {...a11yProps(2)} />}
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <AvailabilityPreferences />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <PasskeyManagement />
          </TabPanel>

          {isAgency && agencyLoginEnabled && (
            <TabPanel value={tabValue} index={2}>
              <AuthorizedReporters />
            </TabPanel>
          )}
        </Paper>
      </Container>

      <Footer />
    </Box>
  );
};

export default AccountSettingsPage;